<template>
    <div 
        class="node-root" 
        :style="`${hovered ? 'z-index: 9 !important;' : 'z-index: 2 !important;'}`" 
        v-if="!filtered"
        @mouseenter="hovered=true; $emit('beginhover', node)"
        @mouseleave="hovered=false; $emit('endhover', node)"
        @touchstart="hovered=true; $emit('beginhover', node)"
        @touchend="hovered=false; $emit('endhover', node)" 
        @click.stop="onNodeClick"
        @keydown.enter="onNodeClick"
    >
        <div
            ref="nodeSubroot"
            class="node-subroot"
            :style="`left: ${startX-cameraStartX}px; top: ${yPos}px;`"
        >
            <div 
                :id="`nodeCircle${node.nodeid}`" 
                ref="nodeCircle" 
                role="button" 
                :tabindex="tabIndex" 
                :aria-label="`${name}, from ${dateStart.getUTCBCString(node.dateOptions)} to ${dateEnd.getUTCBCString(node.dateOptions)}`" 
                class="node-circle" 
                :style="`background-color: ${bgColor}; transition: transform 250ms; transform: scale(${circleScale});/*box-shadow: 0px 0px 10px ${bgColor};*/`"
                @focus="(ev) => { ev.preventDefault(); $emit('scrolltonode', node); }"
            >
                <!-- selected halo/highlight -->
                <div
                    v-show="selected"
                    :style="`position: absolute; width: 100%; height: 100%; border-radius: 50%; transform: scale(1.5); background-color: #DDB23D; opacity: 0.7; z-index: 2 !important;`"
                />
                <!-- important halo/circle -->
                <div
                    v-if="node && node.important"
                    :style="`pointer-events: none; position: absolute; width: 100%; height: 100%; border-radius: 50%; border: 1px solid ${bgColor}; transform: scale(1.25); background-color: transparent; z-index: 3 !important;`"
                />
                <div
                    v-if="node && nodeImage && nodeImage.trim() != '' && !displayResizedImage"
                    :style="`position: absolute; transition: 250ms; border-radius: 50%; z-index: 3 !important; width: 100%; height: 100%;${imageText};opacity: ${!this.categoryTag || !this.categoryTag.filtered ? 0.9 : 0}; transform: scale(0.9);`"
                />
                <div
                    v-show="node && nodeImage && nodeImage.trim() != '' && displayResizedImage"
                    :style="`overflow: hidden; position: absolute; transition: 250ms; border-radius: 50%; z-index: 3 !important; width: 100%; height: 100%;opacity: ${!this.categoryTag || !this.categoryTag.filtered ? 0.9 : 0}; transform: scale(0.9);`"
                >
                    <!--:style="`${resizedDimensionsSame ? 'width: 100%; height: 100%;' : (resizedWidthGreater ? `left: -${resizeOffset}%; height: 100%;` : `top: -${resizeOffset}%; width: 100%;`)} position: absolute;`"-->
                    <canvas
                        ref="resizeCanvas"
                        style="position: absolute; top: 0px; left: 0px; width: 100%; height: 100%;"
                    />
                </div>
            </div>
            <div
                :id="`nodeBar${node.nodeid}`"
                ref="nodeBar"
                class="node-bar"
                :style="`background-color: ${bgColor}; width: ${tailLength}px; transform: scaleY(${circleScale});/*box-shadow: 0px 0px 10px ${bgColor};*/`"
            />
            <div
                ref="nodeText"
                aria-hidden="true"
                class="node-text noselect"
                :style="`transition: transform 250ms; left: ${ Math.min(-Math.min((startX - cameraStartX) + circleRadius / 2 - 10, 0), Math.max(tailLength - titleSize - circleRadius - 10, 0)) }px; ${hovered ? 'transform: scale(1.75) translateZ(1000in) translateX(22%);' : ''} margin-left: ${2 + ((circleRadius / 2) * circleScale)}px`"
            >
                {{ name }}<!-- {{node.getTextSize()}}-->
                <!--({{Math.min(Math.max(-(startX - cameraStartX), tailLength), 0)}})-->
            </div>
        </div>
        <b-popover 
            v-if="presentationMode && $store.state.project.presentationFullSlide < 0"
            :show.sync="showMiniSlide" 
            :target="`nodeCircle${node.nodeid}`" 
            placement="auto"
        >
            <mini-event-slide :nodeid="node.nodeid" />
        </b-popover>
    </div>
</template>

<script>
import Vue from 'vue';
import Node from '@/models/Node';
import Vector2 from '@/models/Vector2';
import Mutations from '@/store/mutations';
import {CalculateNodeTextSize} from '@/util/TextSize';
import MiniEventSlide from '@/components/presentation_mode/MiniEventSlide.vue';
import handleImageSpecialCases from '@/util/ImageUtil';
import KeyListener from '@/KeyListener';
import { getLegacyTagColor } from '@/util/LegacyTagColors';

export default Vue.extend(
    {
        refs: ['nodeSubroot', 'nodeText', 'nodeBar', 'resizeCanvas'],
        components: 
	{
	    MiniEventSlide
	},
        props: 
	{
	    node : Object,
	    camera : Object,
	    trackCnt : Number,
	    rootHeight : Number,
	    //trackHeight : Number,
	    circleRadius : Number,
	    tabIndex : Number,
	},
        data()
        {
            return {
                // Switches for improved reactivity
                _fPosUpdate: false,
                _fDateUpdate: false,
			
                // Actual data
                // ...
                isDestroyed: false,
                crTimeout: null,
                hovered: false,
                showMiniSlide: false,
                displayResizedImage: false,
                resizedImageSource: ''
            };
        },
        computed: 
	{
	    trackHeight()
	    {
	        let computed = this.rootHeight / this.trackCnt;
	        //return computed;
	        return Math.min(computed, this.rootHeight / 4);
	    },
	    topBorder()
	    {
	        return 32;// +this.trackHeight / 2
	    },
	    bottomBorder()
	    {
	        return 80 + this.trackHeight / 2;
	    },
	    maxHeight()
	    {
	        return this.trackCnt * this.trackHeight;
	    },
	    renderHeight()
	    {
	        let perc = (this.maxHeight - this.topBorder - this.bottomBorder) / this.maxHeight;
	        return this.maxHeight * perc / this.trackCnt;
	    },
	    actualTrackHeight()
	    {
	        return this.renderHeight / this.trackCnt;
	    },

	    // Convenience getters for camera properties
	    yearToPixels() 
	    {
	        return (this).camera.yearToPixels;
	    },
	    cameraStartX() 
	    {
	        return this.camera.minDate * this.yearToPixels;
	    },
		
	    // Convenience getters for node
	    nodePosition() 
	    {
	        this._fPosUpdate;
	        return this.node.position;
	    },

	    // CSS properties
	    startX() 
	    {
	        this._fPosUpdate;
	        this._fDateUpdate;
			
	        let pixelsPerYear = this.yearToPixels; // TODO: this should get passed directly but whatever
	        // 31556926 == seconds in a year
	        let pixelsPerSecond = pixelsPerYear / 31556926;
	        let secondsSinceEpoch = (this.dateStart.getTime() / 1000);
	        let secondsSinceYearZero = secondsSinceEpoch + 62167219200;

	        return (pixelsPerSecond * secondsSinceYearZero);
	    },
	    tailLength() 
	    {
	        let pixelsPerYear = this.yearToPixels; // TODO: this should get passed directly but whatever
	        let pixelsPerSecond = pixelsPerYear / 31556926;
	        let secondsSinceEpoch = (this.dateStart.getTime() / 1000);
	        let secondsSinceYearZero = secondsSinceEpoch + 62167219200;

	        return ((this.dateEnd.getTime() / 1000) - secondsSinceEpoch) * pixelsPerSecond;
	    },
	    bgColor() 
	    {
	        let tag = this.categoryTag;
	        if (tag)
	        {
	            if (tag.filtered)
	            {
	                return `rgb(32, 32, 32)`;
	            }
				
	            let tgCol = tag.color;
	            if (!tgCol)
	            {
	                tgCol = getLegacyTagColor(tag.tagid);
	            }
	            return `rgb(${tgCol.r}, ${tgCol.g}, ${tgCol.b})`;
	        }
	        return '#9299A5';
	    },
	    selected() 
	    {
	        return this.$store.state.project.selected === this.node;
	    },
	    presentationMode() 
	    {
	        let pm = this.$store.state.project.presentationMode;
	        if (!pm)
	        {
	            this.showMiniSlide = false;
	        }
	        return pm;
	    },
	    filtered() 
	    {
	        return this.categoryTag && this.categoryTag.filtered;
	    }
	},
        watch: 
	{
	    circleRadius()
	    {
	        if (this.crTimeout) clearTimeout(this.crTimeout);
	        this.crTimeout = setTimeout(()=>
	        {
	            this.$recompute('titleSize');
	            this.crTimeout = null;
	        }, 16);
	    },
	    filtered(newVal, oldVal)
	    {
	        this.$nextTick(()=>
	        {
	            if (oldVal)
	            {
	                this.updateCanvas(true);
	            }
	        });
	    }
	},
        recomputed: 
	{
	    nodeImage()
	    {
	        return this.getNodeImage();
	    },
	    sourceImage()
	    {
	        let result = this.updateCanvas();
	        if (result) return result;
	        return this.node.image;
	    },
	    imageText()
	    {
	        if (this.nodeImage != '')
	        {
	            /** @type {String} */
	            let lower = this.nodeImage.toLowerCase();
	            if (lower.includes('.gif'))
	            {
	                this.displayResizedImage = false;
	                return `background-image: url(${this.nodeImage}); background-size: cover; background-position: center;`;
	            }

	            // Reactivity hack
	            if (this.nodeImage != this.resizedImageSource)
	            {
	                this.$recompute('sourceImage');
	            }

	            let src = this.sourceImage;
	            if (src)
	            {
	                return `background-image: url(${src}); background-size: cover; background-position: center;`;
	            }
	        }
	        return '';
	    },
	    dateStart() 
	    {
	        this._fDateUpdate;
	        //console.log(`${this.node}: ${this.tabIndex}`);
	        return this.node.dateStart;
	    },
	    dateEnd() 
	    {
	        this._fDateUpdate;
	        return this.node.dateEnd;
	    },
	    name() 
	    {
	        return this.node.name;
	    },
	    titleSize()
	    {
	        if (this.startX && this.dateEnd && this.camera.yearToPixels && this.camera.minDate && this.rootHeight)
	        {}
	        return ((this.$refs.nodeText != null) 
	            ? this.$refs.nodeText.clientWidth 
	            : CalculateNodeTextSize(this.name).x);
	    },
	    categoryTag()
	    {
	        // Attempt to force reactivity
	        for(let tag of this.node.tags)
	        {
	            if (tag.type == "category")
	            {
	                return tag;
	            }
	        }
	        return null;
	    },
	    // Required for layout algorithm
	    bound() 
	    {
	        //console.log('recomputing bound');
	        // Reactivity
	        if (this.startX && this.dateEnd && this.camera.yearToPixels && this.camera.minDate && this.rootHeight)
	        {}
	        return this.calculateBounds();
	    },
	    yPos() 
	    {
	        if (this.bound && !this.bound)
	        {
	            console.log('bleh');
	        }
	        return this.rootHeight - this.node.position.y - 45;
	    },
	    circleScale()
	    {
	        if (this.startX && this.dateEnd && this.camera.yearToPixels && this.camera.minDate && this.rootHeight)
	        {}
	        return (this.node.size + 10) / (this.circleRadius + 15);
	    },
	},
        beforeMount()
        {
            this.$set(this.node, 'component', this);
            this.$set(this.node, 'tags', this.node.tags);
        },
        mounted() 
        {
            this.$set(this.node, 'component', this);
            this.forceUpdatePosition();
            this.forceUpdateDates();
		
            let lastStart = this.node.dateStart;
            let lastEnd = this.node.dateEnd;
		
            KeyListener.addKeyHandler(this);
		
            let image = this.node.image;
		
            let loop = () =>
            {
                if (this.isDestroyed)
                {
                    return;
                }

                if (lastStart != this.node.dateStart || lastEnd != this.node.dateEnd)
                {
                    lastStart = this.node.dateStart;
                    lastEnd = this.node.dateEnd;
				
                    this.forceUpdateDates();
                }

                requestAnimationFrame(loop);
            };

            // TODO: This is a temporary hack because I can't for the
            // life of me figure out why reactivity on images is broken.
            setInterval(()=>
            {
                if (this.isDestroyed) return;
                let img = this.getNodeImage();
                if (img != image)
                {
                    image = img;
                    this.$recompute('nodeImage');
                    this.$recompute('sourceImage');
                    this.updateCanvas();
                }
            }, 500);
            //requestAnimationFrame(loop);
        },
        beforeDestroy()
        {
            KeyListener.removeKeyHandler(this);
            this.isDestroyed = true;
            this.$set(this.node, 'component', null);
        },
        methods: 
	{
	    getNodeImage()
	    {
	        return this.node.image;
	        //return handleImageSpecialCases(this.node.image);
	    },
	    updateCanvas(force)
	    {
	        let canvas = this.$refs.resizeCanvas;
	        if (canvas)
	        {
	            if (!force && this.nodeImage == this.resizedImageSource)
	            {
	                if (this.displayResizedImage)
	                {
	                    return null;
	                }
	            }
	            else
	            {
	                this.displayResizedImage = false;
	                const img = new Image();
	                //img.crossOrigin = "anonymous";
	                img.onload = ()=>
	                {
	                    const baseSize = 128;
	                    if (img.width > baseSize || img.height > baseSize)
	                    {
	                        let tgWidth = img.width;
	                        let tgHeight = img.height;

	                        let ratio;
	                        if (tgWidth > baseSize && tgHeight > baseSize)
	                        {
	                            let biggest = Math.min(tgWidth, tgHeight);
	                            ratio = baseSize / biggest;
	                            tgWidth *= ratio;
	                            tgHeight *= ratio;
	                        }
	                        else if (tgWidth > baseSize)
	                        {
	                            ratio = img.width / baseSize;
	                            tgWidth *= ratio;
	                            tgHeight *= ratio;
	                        }
	                        else if (tgHeight > baseSize)
	                        {
	                            ratio = img.height / baseSize;
	                            tgWidth *= ratio;
	                            tgHeight *= ratio;
	                        }
							
	                        /** @type {HTMLCanvasElement} */
	                        canvas.width = baseSize;
	                        canvas.height = baseSize;
							
	                        let ctxt = canvas.getContext("2d");
	                        ctxt.clearRect(0, 0, canvas.width, canvas.height);

	                        let xDiff = Math.abs(canvas.width - tgWidth);
	                        let yDiff = Math.abs(canvas.height - tgHeight);
	                        ctxt.drawImage(img,  -xDiff / 2, -yDiff / 2, tgWidth, tgHeight);
	                        this.displayResizedImage = true;
	                    }
	                };
	                img.src = this.node.image;
	                this.resizedImageSource = this.node.image;
	            }
	        }
	    },
	    calculateBounds() 
	    {
	        let node = this.node;
	        if (!this.$refs.nodeSubroot || !this.$refs.nodeText || !this.$refs.nodeBar)
	        {
	            return {
	                min: {x: Math.floor(node.dateStart)},
	                max: {x: Math.floor(node.dateEnd)}
	            };
	        }

	        /** @type {DOMRect} */
	        let rect = (this).$refs.nodeSubroot.getBoundingClientRect();
	        let rect2 = (this).$refs.nodeText.getBoundingClientRect();
	        let rect3 = (this).$refs.nodeBar.getBoundingClientRect();
	        let rect4 = (this).$refs.nodeCircle.getBoundingClientRect();

	        return {
	            min: {
	                x: Math.min(rect.left, rect2.left, rect3.left, rect4.left)
	            },
	            max: {
	                x: Math.max(rect.right, rect2.right, rect3.right, rect4.right)
	            }
	        };
	    },
	    forceUpdateTags()
	    {
	        this.$recompute('categoryTag');
	    },
	    forceUpdatePosition() 
	    {
	        //this.$set(this, '_fPosUpdate', !this._fPosUpdate)
	        this._fPosUpdate = !this._fPosUpdate;
	    },
	    forceUpdateDates() 
	    {
	        //this.$set(this, '_fDateUpdate', !this._fDateUpdate)
	        this._fDateUpdate = !this._fDateUpdate;
	        //this.forceUpdatePosition();
	        this.$recompute('dateStart');
	        this.$recompute('dateEnd');
	        this.$emit('regen-layout');
	        this.$forceUpdate();
	    },
	    forceUpdateRender()
	    {
	        this.forceUpdatePosition();
	        this.forceUpdateDates();
	        this.$recompute('name');
	    },
	    onNodeClick(mouseevent, forceSelected = false) 
	    {
	        if (this.presentationMode)
	        {
	            this.showMiniSlide = forceSelected ? true : !(this.showMiniSlide);
	            // keep selectedness consistent with showMiniSlide when clicking the node
	            if (this.showMiniSlide && this.$store.state.project.selected !== this.node)
	            {
	                this.$store.commit(Mutations.setSelected, this.node);
	            }
	            else
	            {
	                this.$store.commit(Mutations.setSelected, null);
	            }
	        }
	        else
	        {
	            this.$store.commit(Mutations.setSelected, this.node);
	        }
	    },
	    keyHandler(event, isTextBoxFocused)
	    {
	        if (KeyListener.isTabDown())
	        {
	            // if (document.activeElement === this.$refs.nodeCircle)
	            // {
	            // 	event.preventDefault();
	            // 	setTimeout(() =>
	            // 	{
	            // 		document.activeElement = this.$refs.nodeCircle;
	            // 	}, 600);
	            // }
	        }
	    }
	}
    });
</script>

<style lang="scss">
$circle-radius: var(--circle-radius);
$line-height: var(--line-radius);

.node-root
{
	position: absolute;
	//margin-left: -$circle-radius / 2;
	transition: filter 250ms;
}

.node-root:hover
{
	transition-delay: .08s;
	cursor: pointer;
	filter: brightness(1.3) drop-shadow(0 0 .15rem rgba(0, 0, 0, .9));
}

.node-subroot
{
	//transition: left 100ms ease-out;
	//transition: left 500ms ease;
	transition: top 500ms ease-in-out;
	position: absolute;
}

.node-bar
{
	//transition: 1s;
	transition: background-color 250ms;
	position: absolute;
	height: $line-height;
	top: $circle-radius / 2 - $line-height / 2;
	left: $circle-radius / 2;
	color: white;
	z-index: 3;
}

.node-circle
{
	transition: background-color 250ms !important;
	z-index: 4;
	position: absolute;
	background-color: red;
	width: $circle-radius;
	height: $circle-radius;
	border-radius: 50%;
	//left: -calc($circle-radius / 2);
	left: var(--circle-x-offset);
	top: var(--circle-y-offset);
}

.node-circle:focus
{
	transition: 200ms;
	box-shadow: 0 0 0 5pt #427ad6!important;
}

.node-text
{
	//transition: font-size 100ms ease;
	z-index: 5;
	display: inline-block;
	width: max-content;
	position: absolute;
	font-size: var(--node-font-size);
	font-weight: bold;
	color: #fafcfe;
	//mix-blend-mode: difference;
	
	// Trying to layer the white outline
    text-shadow:
		-1px -1px 0px rgba(16, 16, 16, .75),
		1px -1px 0px rgba(16, 16, 16, .75),
		1px 1px 0px rgba(16, 16, 16, .75),
		-1px 1px 0px rgba(16, 16, 16, .75),
		0 0 10px rgba(0,0,0,0.6);
		//0 0 1px white,
		//0 0 2px white,
		//0 0 2px white,
		//0 0 5px white,
	;	
	//margin-left: $circle-radius;
	//margin-left: calc(0px - var(--circle-x-offset));
	//background-color: red;
	//top: var(--inv-node-font-size);
	line-height: $line-height;
	white-space: nowrap; /* Thanks to Herb Caudill comment */
	transition-delay: .33s !important; 
}
</style>